import { useCallback, useState } from 'react';

/** Message shown above a form when one or more of its fields failed validation. */
export const FORM_ERROR_SUMMARY = 'Please fix the errors below.';

type FieldErrorMap<Field extends string> = Partial<Record<Field, string>>;

/**
 * Client-side validation state for a form: a message per field, plus a summary
 * for the banner above the form.
 *
 * This exists because a value the user can see and fix belongs next to the
 * input they need to fix, not in a toast that covers the corner of the screen
 * and then disappears. Errors from the server for the same form still go
 * through `showToast.error` or a banner — this hook is only for what the UI can
 * check before submitting. See the error-handling rules in docs/product_website/ui-design.md.
 *
 * Usage:
 *
 *   const { fieldErrors, formError, validate, clearField } = useFieldErrors<'email' | 'password'>();
 *
 *   if (!validate({ email: !email.includes('@') ? 'Enter a valid email address.' : undefined })) return;
 */
export function useFieldErrors<Field extends string>() {
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap<Field>>({});
  const [formError, setFormError] = useState<string>('');

  /**
   * Applies a set of per-field messages. Entries that are undefined mean the
   * field passed. Returns true when everything passed, so a submit handler can
   * read as `if (!validate({...})) return;`.
   */
  const validate = useCallback((errors: FieldErrorMap<Field>): boolean => {
    const failed = Object.fromEntries(
      Object.entries(errors).filter(([, message]) => !!message),
    ) as FieldErrorMap<Field>;

    setFieldErrors(failed);
    const ok = Object.keys(failed).length === 0;
    setFormError(ok ? '' : FORM_ERROR_SUMMARY);
    return ok;
  }, []);

  /** Clears one field's error — call it from the input's onChange, so the
   *  message goes away as soon as the user starts correcting it. */
  const clearField = useCallback((field: Field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      if (Object.keys(next).length === 0) setFormError('');
      return next;
    });
  }, []);

  /** Clears everything — call before a fresh submit attempt. */
  const clearAll = useCallback(() => {
    setFieldErrors({});
    setFormError('');
  }, []);

  /** Shows a form-level message that isn't tied to a single field (a failed
   *  login, say) without marking any input invalid. */
  const setSummary = useCallback((message: string) => {
    setFormError(message);
  }, []);

  /**
   * The accessibility wiring an input needs to announce its error. Spread it
   * onto the input and give the matching `ErrorMessage` the same id:
   *
   *   <input {...fieldProps('email', 'email-help')} />
   *   {fieldErrors.email && <ErrorMessage variant="field" id="email-error" message={fieldErrors.email} />}
   */
  const fieldProps = useCallback(
    (field: Field, describedByWhenValid?: string) => ({
      'aria-invalid': !!fieldErrors[field],
      'aria-describedby': fieldErrors[field] ? `${field}-error` : describedByWhenValid,
    }),
    [fieldErrors],
  );

  return { fieldErrors, formError, validate, clearField, clearAll, setSummary, fieldProps };
}
